// Implements: F-CNV-012
/**
 * The reference image of a turn (OD-11), prepared in the browser.
 *
 * These checks are a quick answer for the person, nothing more: the server judges the image
 * again (structure, size, format) and stays the authority. The type is read from the FIRST
 * BYTES of the file, never from its name or its declared type, as the server does.
 *
 * The wire form is plain standard base64: no `data:` prefix, no whitespace, no URL-safe
 * alphabet. It is built from the bytes (an ArrayBuffer), so there is no prefix to strip.
 * The digest names the content: a different image is a different intention (another turn id).
 */

/** The server's own ceiling, in decoded bytes. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export type ReferenceImageRefusal = "notSupported" | "tooBig" | "unreadable";

export type ReferenceImageRead =
  | { readonly kind: "ok"; readonly base64: string; readonly digest: string }
  | { readonly kind: "refused"; readonly reason: ReferenceImageRefusal };

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
/** Bytes per `String.fromCharCode` call: a spread of the whole image would overflow the stack. */
const CHUNK = 0x8000;

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  return bytes.length >= signature.length && signature.every((byte, index) => bytes[index] === byte);
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
  }
  return btoa(binary);
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-256", buffer));
  return Array.from(hash, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export async function readReferenceImage(file: File): Promise<ReferenceImageRead> {
  if (file.size > MAX_IMAGE_BYTES) return { kind: "refused", reason: "tooBig" };
  try {
    const head = new Uint8Array(await file.slice(0, PNG_SIGNATURE.length).arrayBuffer());
    if (!startsWith(head, PNG_SIGNATURE) && !startsWith(head, JPEG_SIGNATURE)) {
      return { kind: "refused", reason: "notSupported" };
    }
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    return { kind: "ok", base64: toBase64(bytes), digest: await sha256Hex(buffer) };
  } catch {
    return { kind: "refused", reason: "unreadable" };
  }
}
